import { useEffect, useMemo, useState } from 'react';

import { trpc, TrpcOutputs } from '../../config/trpc';
import { deepMergeObjects } from '../../utils/deep-merge-objects';
import { formatForClient } from '../../utils/format-line-item';

type LineItemOutput = TrpcOutputs['getLineItemsByQuoteVariantId'][0] & {
  children?: LineItemOutput[];
};

export const groupToParent = (lineItems: LineItemOutput[]) => {
  const parents = lineItems.filter((item) => !item.parent_id);
  const kids = lineItems.filter((item) => item.parent_id);

  return parents.map((parent) => {
    const children = kids.filter((child) => child.parent_id === parent.id);
    return { ...parent, children };
  });
};

export const useLineItems = (quoteVariantId?: number) => {
  // Don't query (and thus don't error) if the QV id doesn't exist
  const enabled = quoteVariantId > 0;
  const [lineItems, setLineItems] = useState<LineItemOutput[]>([]);
  const {
    data: initialData,
    isLoading,
    error,
  } = trpc.getLineItemsByQuoteVariantId.useQuery({ quoteVariantId }, { refetchOnWindowFocus: false, enabled });

  useEffect(() => {
    if (initialData) {
      const nextData = initialData?.map(formatForClient);
      setLineItems(nextData);
    }
  }, [initialData]);

  trpc.onLineItemCreated.useSubscription(
    { quoteVariantId },
    {
      onData: (newData: any) => {
        setLineItems((prevData) => [...prevData, formatForClient(newData)]);
      },
      onError: (err: any) => console.log('onLineItemCreated error: ', err),
      enabled,
    },
  );

  trpc.onLineItemUpdated.useSubscription(
    { quoteVariantId },
    {
      onData: (newData: LineItemOutput) => {
        setLineItems((prevData: LineItemOutput[]) => {
          return prevData.map((lineItem: LineItemOutput) => {
            if (lineItem.id !== newData.id) return lineItem;
            return deepMergeObjects(lineItem, formatForClient(newData));
          });
        });
      },
      onError: (err: any) => console.log('onLineItemUpdated error: ', err),
      enabled,
    },
  );

  trpc.onLineItemDeleted.useSubscription(
    { quoteVariantId },
    {
      onData: (deletedId: number) => {
        setLineItems((prevData: LineItemOutput[]) => {
          return prevData.filter((quote) => quote.id !== deletedId);
        });
      },
      onError: (err: any) => console.log('onLineItemDeleted error: ', err),
      enabled,
    },
  );

  const groupedLineItems = groupToParent(lineItems);
  const totalCost = useMemo(() => {
    return lineItems?.reduce((currTotalCost, currLineItem) => currTotalCost + currLineItem.cost, 0) || 0;
  }, [lineItems]);

  return { lineItems, groupedLineItems, totalCost, isLoading, error };
};
